import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authenticatedRequest } from '../../services/authApi';
import { REST_API_ORIGIN } from '../../platform/endpoints';
import { localizedValue } from '../../localization/presentationLanguage';
const adminText = (es, en) => localizedValue({ es, en });

const ADMIN_FEED_BASE = `${REST_API_ORIGIN}/api/admin/feed`;

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'reviewed', label: 'Revisados' },
  { value: 'ignored', label: 'Ignorados' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Más recientes' },
  { value: 'oldest', label: 'Más antiguos' },
  { value: 'critical', label: 'Más críticos' },
  { value: 'reports', label: 'Más reportes' },
];

const REASON_LABELS = {
  spam: 'Spam',
  harassment: 'Acoso',
  hate: 'Discurso de odio',
  violence: 'Violencia',
  sexual: 'Contenido sexual',
  misinformation: 'Desinformación',
  impersonation: 'Suplantación',
  self_harm: 'Autolesión',
  scam: 'Estafa',
  other: 'Otro',
};

const DANGER_REASONS = ['hate', 'violence', 'sexual', 'self_harm', 'harassment', 'scam'];

function readTokenFromStorage(storage) {
  if (!storage) return '';

  const directCandidates = ['token', 'authToken', 'accessToken', 'jwt', 'zodiacusToken'];
  for (const key of directCandidates) {
    const value = String(storage.getItem(key) || '').trim();
    if (value) return value;
  }

  const jsonCandidates = ['userInfo', 'user', 'auth', 'session'];
  for (const key of jsonCandidates) {
    const raw = String(storage.getItem(key) || '').trim();
    if (!raw) continue;

    try {
      const parsed = JSON.parse(raw);
      const nestedValue =
        parsed?.token ||
        parsed?.authToken ||
        parsed?.accessToken ||
        parsed?.jwt ||
        parsed?.userInfo?.token ||
        parsed?.data?.token ||
        '';

      if (String(nestedValue || '').trim()) {
        return String(nestedValue).trim();
      }
    } catch {
      // ignore malformed payloads
    }
  }

  return '';
}

function resolveAuthToken() {
  if (typeof window === 'undefined') return '';
  return readTokenFromStorage(window.localStorage) || readTokenFromStorage(window.sessionStorage) || '';
}

function getAuthHeaders() {
  const token = resolveAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function getRequestErrorMessage(error, fallback) {
  return error?.response?.data?.message || error?.payload?.message || error?.message || fallback;
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('es-AR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function getPriorityStyle(priority = null) {
  const level = String(priority?.level || '').trim().toLowerCase();

  if (level === 'urgent') {
    return {
      background: 'rgba(255, 107, 107, 0.16)',
      color: '#ffc0c0',
      border: '1px solid rgba(255, 107, 107, 0.28)',
    };
  }

  if (level === 'high') {
    return {
      background: 'rgba(255, 184, 77, 0.14)',
      color: '#ffe0ad',
      border: '1px solid rgba(255, 184, 77, 0.24)',
    };
  }

  if (level === 'medium') {
    return {
      background: 'rgba(176, 132, 255, 0.14)',
      color: '#e7d4ff',
      border: '1px solid rgba(176, 132, 255, 0.24)',
    };
  }

  return {
    background: 'rgba(148, 163, 184, 0.14)',
    color: '#dbe3f1',
    border: '1px solid rgba(148, 163, 184, 0.22)',
  };
}

function formatPrioritySignalLabel(key = '') {
  const safeKey = String(key || '').trim();

  if (safeKey === 'reasonSeverity') return 'Severidad del motivo';
  if (safeKey === 'reportsBoost') return 'Impulso por reportes';
  if (safeKey === 'freshnessBoost') return 'Impulso por recencia';
  if (safeKey === 'postRiskBoost') return 'Riesgo del post';
  if (safeKey === 'postReportCount') return 'Reportes del post';
  return safeKey;
}


function normalizeReport(item, index) {
  const post = item?.post || item?.postSnapshot || {};
const comment = item?.comment || {};
const commentPost = comment?.post && typeof comment.post === 'object' ? comment.post : {};
const resolvedPost = post?._id || post?.id ? post : commentPost;
const author = resolvedPost?.author || item?.postAuthor || {};
const commentAuthor = comment?.author || {};
const reporter = item?.reportedBy || item?.reporter || {};

  const reportId = item?._id || item?.id || `report-${index}`;
  const postId = resolvedPost?._id || resolvedPost?.id || item?.postId || item?.feedPostId || '';
  const reasonKey = String(item?.reason || item?.category || 'other').trim().toLowerCase();
  const rawStatus = String(item?.status || 'pending').trim().toLowerCase();
  const targetType = String(item?.targetType || (comment?._id || comment?.id ? 'comment' : 'post')).trim().toLowerCase();
  const status = ['pending', 'reviewed', 'ignored'].includes(rawStatus) ? rawStatus : 'pending';
  const details = String(item?.details || item?.description || item?.message || '').trim();
 const postText = String(resolvedPost?.text || resolvedPost?.content || resolvedPost?.body || item?.postText || resolvedPost?.caption || '').trim();
const commentText = String(comment?.text || comment?.body || item?.commentText || '').trim();
  const createdAt = item?.createdAt || item?.reportedAt || item?.insertedAt || null;
  const reviewedAt = item?.reviewedAt || null;
  const postReportCount =
    Number(post?.reportCount ?? item?.postReportCount ?? item?.totalReports ?? item?.reportsCount ?? 1) || 1;

  const normalized = {
    raw: item,
    reportId,
targetType,
postId,
commentId: comment?._id || comment?.id || item?.commentId || '',
    reasonKey,
    reasonLabel: REASON_LABELS[reasonKey] || item?.reasonLabel || 'Otro',
    status,
    details,
    createdAt,
    reviewedAt,
    postReportCount,
    postText,
commentText,
authorName:
  author?.fullName || author?.name || author?.username || author?.nickname || resolvedPost?.authorName || 'Usuario',
authorId: author?._id || author?.id || resolvedPost?.authorId || '',
commentAuthorName:
  commentAuthor?.fullName || commentAuthor?.name || commentAuthor?.username || commentAuthor?.nickname || 'Usuario',
commentAuthorId: commentAuthor?._id || commentAuthor?.id || '',
    reporterName:
      reporter?.fullName || reporter?.name || reporter?.username || reporter?.nickname || 'Usuario',
    reporterId: reporter?._id || reporter?.id || item?.reportedById || '',
    reviewedByName:
      item?.reviewedBy?.fullName || item?.reviewedBy?.name || item?.reviewedBy?.username || '—',
    canShowReviewedMeta: status !== 'pending' && Boolean(item?.reviewedAt),
  };

  const backendPriority = item?.priority && typeof item.priority === 'object'
    ? item.priority
    : null;

  return {
    ...normalized,
    priority: {
      level: String(backendPriority?.level || 'low').trim().toLowerCase(),
      label: String(backendPriority?.label || 'Baja').trim(),
      score: Number(backendPriority?.score || 0),
      signals: backendPriority?.signals && typeof backendPriority.signals === 'object'
        ? backendPriority.signals
        : {},
      scoreBreakdown: backendPriority?.signals && typeof backendPriority.signals === 'object'
        ? Object.entries(backendPriority.signals)
            .filter(([key]) => key !== 'postReportCount')
            .map(([key, value]) => ({
              key,
              label: formatPrioritySignalLabel(key),
              value: Number(value || 0),
            }))
        : [],
    },
  };
}

async function fetchFeedReports() {
  const payload = await authenticatedRequest(`${ADMIN_FEED_BASE}/reports`, {
    headers: {
      ...getAuthHeaders(),
    },
    diagSource: 'AdminFeedReports.fetchReports',
  });
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.reports)) return payload.reports;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

async function markReportReviewed(reportId) {
  return authenticatedRequest(
    `${ADMIN_FEED_BASE}/reports/${reportId}/review`,
    {
      method: 'PATCH',
      body: {},
      headers: {
        ...getAuthHeaders(),
      },
    }
  );
}

async function ignoreReport(reportId) {
  return authenticatedRequest(
    `${ADMIN_FEED_BASE}/reports/${reportId}/ignore`,
    {
      method: 'PATCH',
      body: {},
      headers: {
        ...getAuthHeaders(),
      },
    }
  );
}

async function deleteReportedPost(postId) {
  return authenticatedRequest(`${ADMIN_FEED_BASE}/posts/${postId}`, {
    method: 'DELETE',
    headers: {
      ...getAuthHeaders(),
    },
  });
}

async function deleteReportedComment(commentId) {
  return authenticatedRequest(`${ADMIN_FEED_BASE}/comments/${commentId}`, {
    method: 'DELETE',
    headers: {
      ...getAuthHeaders(),
    },
  });
}

function getStatusBadgeStyle(status) {
  if (status === 'reviewed') {
    return {
      background: 'rgba(54, 179, 126, 0.16)',
      color: '#8ff0b1',
      border: '1px solid rgba(54, 179, 126, 0.28)',
    };
  }

  if (status === 'ignored') {
    return {
      background: 'rgba(148, 163, 184, 0.14)',
      color: '#dbe3f1',
      border: '1px solid rgba(148, 163, 184, 0.22)',
    };
  }

  return {
    background: 'rgba(255, 184, 77, 0.14)',
    color: '#ffd898',
    border: '1px solid rgba(255, 184, 77, 0.28)',
  };
}

function getReasonBadgeStyle(reasonKey) {
  if (DANGER_REASONS.includes(reasonKey)) {
    return {
      background: 'rgba(255, 107, 107, 0.12)',
      color: '#ffb4b4',
      border: '1px solid rgba(255, 107, 107, 0.22)',
    };
  }

  return {
    background: 'rgba(176, 132, 255, 0.14)',
    color: '#e7d4ff',
    border: '1px solid rgba(176, 132, 255, 0.24)',
  };
}

const baseButtonStyle = {
  border: '1px solid rgba(255,255,255,0.12)',
  background: 'rgba(255,255,255,0.04)',
  color: '#f7f3ff',
  borderRadius: 12,
  padding: '10px 14px',
  fontSize: 13,
  fontWeight: 700,
  cursor: 'pointer',
  transition:
    'transform 180ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 180ms ease, border-color 180ms ease, background 180ms ease, opacity 180ms ease',
};

const panelCardStyle = {
  borderRadius: 22,
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.08)',
  boxShadow: '0 18px 44px rgba(0,0,0,0.22)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
};

function AdminFeedReportsScreen() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [sortBy, setSortBy] = useState('critical');
  const [search, setSearch] = useState('');
  const [activeActionKey, setActiveActionKey] = useState('');
  const navigate = useNavigate();

  const [viewportWidth, setViewportWidth] = useState(() => {
    if (typeof window === 'undefined') return 1280;
    return window.innerWidth || 1280;
  });

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setViewportWidth(window.innerWidth || 1280);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isTablet = viewportWidth <= 980;
  const isMobile = viewportWidth <= 640;

  const loadReports = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const items = await fetchFeedReports();
      setReports(items.map(normalizeReport));
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'No se pudieron cargar los reportes del feed.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let intervalId = null;

    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') {
        loadReports();
      }
    };

    intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadReports();
      }
    }, 15000);

    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener('visibilitychange', refreshIfVisible);

    return () => {
      if (intervalId) window.clearInterval(intervalId);
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [loadReports]);

  useEffect(() => {
    if (!success) return undefined;
    const timeout = window.setTimeout(() => setSuccess(''), 2800);
    return () => window.clearTimeout(timeout);
  }, [success]);

  const filteredReports = useMemo(() => {
    const term = search.trim().toLowerCase();

    const nextReports = reports.filter((report) => {
      const matchesStatus = statusFilter === 'all' ? true : report.status === statusFilter;
      if (!matchesStatus) return false;

      if (!term) return true;

      const haystack = [
        report.reasonLabel,
        report.authorName,
        report.commentAuthorName,
        report.reporterName,
        report.postText,
        report.commentText,
        report.details,
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });

    return [...nextReports].sort((a, b) => {
      if (sortBy === 'oldest') {
        return new Date(a?.createdAt || 0).getTime() - new Date(b?.createdAt || 0).getTime();
      }

      if (sortBy === 'reports') {
        if (b.postReportCount !== a.postReportCount) return b.postReportCount - a.postReportCount;
      }

      if (sortBy === 'critical') {
                if (Number(b.priority?.score || 0) !== Number(a.priority?.score || 0)) {
          return Number(b.priority?.score || 0) - Number(a.priority?.score || 0);
        }
        if (b.postReportCount !== a.postReportCount) return b.postReportCount - a.postReportCount;
      }

      const bTime = new Date(b?.createdAt || 0).getTime();
      const aTime = new Date(a?.createdAt || 0).getTime();
      return bTime - aTime;
    });
  }, [reports, search, sortBy, statusFilter]);

  const stats = useMemo(() => {
    return reports.reduce(
      (acc, report) => {
        acc.total += 1;
        acc[report.status] += 1;
                if (report.priority?.level === 'urgent' || report.priority?.level === 'high') acc.critical += 1;
        if (report.postReportCount >= 3) acc.escalated += 1;
        return acc;
      },
      {
        total: 0,
        pending: 0,
        reviewed: 0,
        ignored: 0,
        critical: 0,
        escalated: 0,
      }
    );
  }, [reports]);

  const dashboardCards = useMemo(
    () => [
      {
        key: 'total',
        label: 'Total reportes',
        value: stats.total,
        accent: 'rgba(255,224,130,0.20)',
        glow: 'rgba(255,224,130,0.16)',
      },
      {
        key: 'pending',
        label: 'Pendientes',
        value: stats.pending,
        accent: 'rgba(255,184,77,0.22)',
        glow: 'rgba(255,184,77,0.14)',
      },
      {
        key: 'reviewed',
        label: 'Revisados',
        value: stats.reviewed,
        accent: 'rgba(54,179,126,0.20)',
        glow: 'rgba(54,179,126,0.14)',
      },
      {
        key: 'ignored',
        label: 'Ignorados',
        value: stats.ignored,
        accent: 'rgba(148,163,184,0.18)',
        glow: 'rgba(148,163,184,0.12)',
      },
      {
        key: 'critical',
        label: 'Críticos',
        value: stats.critical,
        accent: 'rgba(255,107,107,0.22)',
        glow: 'rgba(255,107,107,0.14)',
      },
      {
        key: 'escalated',
        label: 'Escalados',
        value: stats.escalated,
        accent: 'rgba(176,132,255,0.20)',
        glow: 'rgba(176,132,255,0.14)',
      },
    ],
    [stats]
  );

  const activeFilterLabel = useMemo(() => {
    if (statusFilter === 'all') return 'Todos los estados';
    return STATUS_OPTIONS.find((option) => option.value === statusFilter)?.label || 'Pendientes';
  }, [statusFilter]);

  const activeSortLabel = useMemo(() => {
    return SORT_OPTIONS.find((option) => option.value === sortBy)?.label || 'Más críticos';
  }, [sortBy]);

  const hasActiveFilters = Boolean(search.trim()) || statusFilter !== 'pending' || sortBy !== 'critical';

  const handleReview = useCallback(
    async (reportId) => {
      setActiveActionKey(`review-${reportId}`);
      setError('');

      try {
        await markReportReviewed(reportId);
        setSuccess('Reporte marcado como revisado.');
        await loadReports();
      } catch (err) {
        setError(getRequestErrorMessage(err, 'No se pudo marcar el reporte como revisado.'));
      } finally {
        setActiveActionKey('');
      }
    },
    [loadReports]
  );

  const handleIgnore = useCallback(
    async (reportId) => {
      setActiveActionKey(`ignore-${reportId}`);
      setError('');

      try {
        await ignoreReport(reportId);
        setSuccess('Reporte ignorado correctamente.');
        await loadReports();
      } catch (err) {
        setError(getRequestErrorMessage(err, 'No se pudo ignorar el reporte.'));
      } finally {
        setActiveActionKey('');
      }
    },
    [loadReports]
  );

  const handleDeletePost = useCallback(
    async (postId) => {
      if (!postId) {
        setError('Este reporte no tiene un post asociado válido para eliminar.');
        return;
      }

      const confirmed = window.confirm(
        '¿Seguro que querés eliminar este post reportado? Esta acción no se puede deshacer.'
      );

      if (!confirmed) return;

      setActiveActionKey(`delete-${postId}`);
      setError('');

      try {
        await deleteReportedPost(postId);
        setSuccess('Post eliminado correctamente.');
        await loadReports();
      } catch (err) {
        setError(getRequestErrorMessage(err, 'No se pudo eliminar el post reportado.'));
      } finally {
        setActiveActionKey('');
      }
    },
    [loadReports]
  );

  const handleDeleteComment = useCallback(
  async (commentId) => {
    if (!commentId) {
      setError('Este reporte no tiene una respuesta asociada válida para eliminar.');
      return;
    }

    const confirmed = window.confirm(
      '¿Seguro que querés eliminar esta respuesta reportada? Esta acción no se puede deshacer.'
    );

    if (!confirmed) return;

    setActiveActionKey(`delete-comment-${commentId}`);
    setError('');

    try {
      await deleteReportedComment(commentId);
      setSuccess('Respuesta eliminada correctamente.');
      await loadReports();
    } catch (err) {
      setError(getRequestErrorMessage(err, 'No se pudo eliminar la respuesta reportada.'));
    } finally {
      setActiveActionKey('');
    }
  },
  [loadReports]
);


  return (
    <div
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at top, rgba(148, 87, 255, 0.20), transparent 24%), radial-gradient(circle at 85% 10%, rgba(255, 214, 122, 0.10), transparent 18%), linear-gradient(180deg, #120b1f 0%, #090510 100%)',
        color: '#f8f5ff',
        padding: '32px 20px 60px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 1280,
          margin: '0 auto',
        }}
      >
        <div
          style={{
            ...panelCardStyle,
            padding: isMobile ? '18px 16px 18px' : isTablet ? '20px 18px 20px' : '24px 24px 22px',
            marginBottom: 24,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background:
                'radial-gradient(360px 160px at 0% 0%, rgba(255,224,130,0.12), rgba(255,224,130,0) 58%), radial-gradient(320px 180px at 100% 0%, rgba(176,132,255,0.14), rgba(176,132,255,0) 58%)',
            }}
          />

          <div
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              gap: isMobile ? 16 : 22,
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{
                minWidth: 0,
                width: '100%',
                flex: '1 1 640px',
              }}
            >
              <button
                type="button"
                onClick={() => navigate('/home')}
                style={{
                  ...baseButtonStyle,
                  marginBottom: 14,
                  padding: '8px 12px',
                  minHeight: 36,
                  borderRadius: 999,
                  background: 'linear-gradient(135deg, rgba(36, 24, 58, 0.92), rgba(64, 39, 104, 0.86))',
                  border: '1px solid rgba(218,165,32,0.28)',
                  color: '#FFE49A',
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: 0.2,
                  boxShadow: '0 8px 18px rgba(0,0,0,0.18)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span aria-hidden="true">←</span>
                <span>{adminText('Volver al Home', 'Back to Home')}</span>
              </button>

              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  borderRadius: 999,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#e7d4ff',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  marginBottom: 14,
                }}
              >
                {adminText('Moderación · Feed Zodiacus', 'Moderation · Zodiacus Feed')}
              </div>

              <h1
                style={{
                  margin: 0,
                  fontSize: isMobile ? 'clamp(22px, 8vw, 30px)' : 'clamp(28px, 3.4vw, 42px)',
                  lineHeight: 1.04,
                  letterSpacing: isMobile ? -0.4 : -0.9,
                  wordBreak: 'break-word',
                }}
              >
                {adminText('Panel de reportes', 'Reports dashboard')}
              </h1>

              <p
                style={{
                  margin: '12px 0 0',
                  maxWidth: 760,
                  color: 'rgba(243, 239, 255, 0.76)',
                  fontSize: isMobile ? 14 : 15,
                  lineHeight: 1.6,
                }}
              >
                {adminText('Revisá denuncias del feed, evaluá el contenido reportado y tomá acciones rápidas de moderación sin salir del panel.', 'Review feed reports, evaluate reported content, and take moderation action without leaving the dashboard.')}
              </p>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 10,
                  marginTop: 16,
                }}
              >
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#F8F3FF',
                    fontSize: 12.5,
                    fontWeight: 800,
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: loading ? '#FFD700' : '#8ff0b1',
                      boxShadow: loading
                        ? '0 0 10px rgba(255,215,0,0.50)'
                        : '0 0 10px rgba(143,240,177,0.36)',
                    }}
                  />
                  {loading ? 'Sincronizando panel' : `${filteredReports.length} visibles ahora`}
                </div>

                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#F8F3FF',
                    fontSize: 12.5,
                    fontWeight: 800,
                  }}
                >
                  Estado: {activeFilterLabel}
                </div>

                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderRadius: 999,
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#F8F3FF',
                    fontSize: 12.5,
                    fontWeight: 800,
                  }}
                >
                  Orden: {activeSortLabel}
                </div>

                {hasActiveFilters ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSearch('');
                      setStatusFilter('pending');
                      setSortBy('critical');
                    }}
                    style={{
                      ...baseButtonStyle,
                      borderRadius: 999,
                      padding: '8px 12px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      fontSize: 12.5,
                      fontWeight: 800,
                    }}
                  >
                    Limpiar filtros
                  </button>
                ) : null}
              </div>
            </div>

            <button
              type="button"
              onClick={loadReports}
              disabled={loading}
              style={{
                ...baseButtonStyle,
                minWidth: isMobile ? '100%' : 170,
                width: isMobile ? '100%' : 'auto',
                alignSelf: 'flex-start',
                background: 'linear-gradient(135deg, rgba(189, 144, 255, 0.22), rgba(255, 214, 122, 0.14))',
                border: '1px solid rgba(255,255,255,0.14)',
                boxShadow: '0 12px 24px rgba(0,0,0,0.18)',
              }}
            >
              {loading ? 'Actualizando...' : 'Actualizar panel'}
            </button>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: isMobile ? 12 : 14,
            marginBottom: 24,
          }}
        >
          {dashboardCards.map((item) => (
            <div
              key={item.key}
              style={{
                ...panelCardStyle,
                padding: '18px 18px 16px',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: `radial-gradient(220px 120px at 0% 0%, ${item.accent}, transparent 60%)`,
                  pointerEvents: 'none',
                }}
              />

              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ color: 'rgba(243,239,255,0.66)', fontSize: 13, marginBottom: 8 }}>{item.label}</div>
                <div
                  style={{
                    fontSize: 32,
                    fontWeight: 800,
                    letterSpacing: -0.8,
                    textShadow: `0 0 20px ${item.glow}`,
                  }}
                >
                  {item.value}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : '1.25fr 0.75fr 0.75fr',
            gap: isMobile ? 12 : 16,
            marginBottom: 24,
          }}
        >
          <div
            style={{
              ...panelCardStyle,
              padding: isMobile ? 14 : 16,
            }}
          >
            <div style={{ fontSize: 13, color: 'rgba(243,239,255,0.66)', marginBottom: 8 }}>{adminText('Buscar', 'Search')}</div>
            <div style={{ display: 'flex', gap: 10, width: '100%' }}>
              <input
                id="admin-feed-reports-search"
                name="adminFeedReportsSearch"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={adminText('Buscá por motivo, autor, denunciante o texto del post', 'Search by reason, author, reporter, or post text')}
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: 48,
                  borderRadius: 14,
                  border: '1px solid rgba(255,255,255,0.12)',
                  background: 'rgba(9, 5, 16, 0.72)',
                  color: '#fff',
                  padding: '0 14px',
                  outline: 'none',
                  fontSize: 14,
                  boxShadow: '0 10px 18px rgba(0,0,0,0.16) inset',
                }}
              />
              {search.trim() ? (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  style={{
                    ...baseButtonStyle,
                    minWidth: 46,
                    padding: '0 12px',
                    height: 48,
                    borderRadius: 14,
                  }}
                >
                  ✕
                </button>
              ) : null}
            </div>
          </div>

          <div
            style={{
              ...panelCardStyle,
              padding: isMobile ? 14 : 16,
            }}
          >
            <div style={{ fontSize: 13, color: 'rgba(243,239,255,0.66)', marginBottom: 8 }}>Estado</div>
            <select
              id="admin-feed-reports-status"
              name="adminFeedReportsStatus"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              style={{
                width: '100%',
                height: 48,
                borderRadius: 14,
                border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(9, 5, 16, 0.72)',
                color: '#fff',
                padding: '0 14px',
                minWidth: 0,
                outline: 'none',
                fontSize: 14,
                boxShadow: '0 10px 18px rgba(0,0,0,0.16) inset',
              }}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div
            style={{
              ...panelCardStyle,
              padding: isMobile ? 14 : 16,
            }}
          >
            <div style={{ fontSize: 13, color: 'rgba(243,239,255,0.66)', marginBottom: 8 }}>Ordenar</div>
            <select
              id="admin-feed-reports-sort"
              name="adminFeedReportsSort"
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
              style={{
                width: '100%',
                height: 48,
                borderRadius: 14,
                border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(9, 5, 16, 0.72)',
                color: '#fff',
                padding: '0 14px',
                minWidth: 0,
                outline: 'none',
                fontSize: 14,
                boxShadow: '0 10px 18px rgba(0,0,0,0.16) inset',
              }}
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error ? (
          <div
            style={{
              marginBottom: 18,
              borderRadius: 16,
              padding: '14px 16px',
              background: 'rgba(255, 107, 107, 0.12)',
              border: '1px solid rgba(255, 107, 107, 0.24)',
              color: '#ffc2c2',
              fontWeight: 600,
            }}
          >
            {error}
          </div>
        ) : null}

        {success ? (
          <div
            style={{
              marginBottom: 18,
              borderRadius: 16,
              padding: '14px 16px',
              background: 'rgba(54, 179, 126, 0.12)',
              border: '1px solid rgba(54, 179, 126, 0.24)',
              color: '#baf3d0',
              fontWeight: 600,
            }}
          >
            {success}
          </div>
        ) : null}

        <div
          style={{
            display: 'grid',
            gap: 16,
          }}
        >
          {loading ? (
            <div
              style={{
                ...panelCardStyle,
                padding: '28px 22px',
                textAlign: 'center',
                color: 'rgba(243,239,255,0.72)',
              }}
            >
              {adminText('Cargando reportes...', 'Loading reports...')}
            </div>
          ) : null}

          {!loading && !filteredReports.length ? (
            <div
              style={{
                ...panelCardStyle,
                padding: '34px 24px',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 10 }}>{adminText('No hay reportes para mostrar', 'No reports to display')}</div>
              <div style={{ color: 'rgba(243,239,255,0.66)', fontSize: 15 }}>
                {adminText('Probá cambiando los filtros o actualizando el panel para traer denuncias nuevas.', 'Try changing the filters or refreshing the dashboard to load new reports.')}
              </div>
            </div>
          ) : null}

          {!loading &&
            filteredReports.map((report) => {
              const isReviewing = activeActionKey === `review-${report.reportId}`;
              const isIgnoring = activeActionKey === `ignore-${report.reportId}`;
             const isDeletingPost = activeActionKey === `delete-${report.postId}`;
const isDeletingComment = activeActionKey === `delete-comment-${report.commentId}`;
const isDeleting = isDeletingPost || isDeletingComment;
              const isDanger = DANGER_REASONS.includes(report.reasonKey);

              return (
                <article
                  key={report.reportId}
                  style={{
                    ...panelCardStyle,
                    padding: 22,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: isDanger
                        ? 'radial-gradient(280px 140px at 0% 0%, rgba(255,107,107,0.09), transparent 58%)'
                        : 'radial-gradient(280px 140px at 0% 0%, rgba(176,132,255,0.09), transparent 58%)',
                      pointerEvents: 'none',
                    }}
                  />

                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 14,
                        flexWrap: 'wrap',
                        marginBottom: 16,
                      }}
                    >
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                        <span
                          style={{
                            ...getStatusBadgeStyle(report.status),
                            padding: '7px 11px',
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          {report.status === 'pending'
                            ? 'Pendiente'
                            : report.status === 'reviewed'
                              ? 'Revisado'
                              : 'Ignorado'}
                        </span>

                        <span
                          style={{
                            ...getReasonBadgeStyle(report.reasonKey),
                            padding: '7px 11px',
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          {report.reasonLabel}
                        </span>

                        <span
                          style={{
                            ...getPriorityStyle(report.priority),
                            padding: '7px 11px',
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 800,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                          }}
                        >
                          <span>{report.priority.label}</span>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: 28,
                              height: 22,
                              padding: '0 7px',
                              borderRadius: 999,
                              background: 'rgba(255,255,255,0.10)',
                              border: '1px solid rgba(255,255,255,0.10)',
                              color: 'inherit',
                              fontSize: 11,
                              fontWeight: 900,
                              lineHeight: 1,
                            }}
                          >
                            {Number(report.priority?.score || 0)}
                          </span>
                        </span>

                        <span
                          style={{
                            padding: '7px 11px',
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 700,
                            background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            color: '#f7f3ff',
                          }}
                        >
                          {report.postReportCount} reporte{report.postReportCount === 1 ? '' : 's'} en este post
                        </span>

                        {report.priority?.signals?.postReportCount ? (
                          <span
                            style={{
                              padding: '7px 11px',
                              borderRadius: 999,
                              fontSize: 12,
                              fontWeight: 700,
                              background: 'rgba(255,255,255,0.05)',
                              border: '1px solid rgba(255,255,255,0.08)',
                              color: '#f7f3ff',
                            }}
                          >
                            Score guiado por {report.priority.signals.postReportCount} reporte{report.priority.signals.postReportCount === 1 ? '' : 's'}
                          </span>
                        ) : null}
                      </div>

                      <div style={{ color: 'rgba(243,239,255,0.58)', fontSize: 13 }}>
                        Reportado el {formatDate(report.createdAt)}
                      </div>
                    </div>

                    {Array.isArray(report.priority?.scoreBreakdown) && report.priority.scoreBreakdown.length > 0 ? (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                          gap: 10,
                          marginBottom: 14,
                        }}
                      >
                        {report.priority.scoreBreakdown.map((signal) => (
                          <div
                            key={`${report.reportId}-${signal.key}`}
                            style={{
                              borderRadius: 16,
                              padding: '12px 13px',
                              background: 'rgba(255,255,255,0.04)',
                              border: '1px solid rgba(255,255,255,0.07)',
                              boxShadow: '0 10px 18px rgba(0,0,0,0.10)',
                            }}
                          >
                            <div
                              style={{
                                fontSize: 11.5,
                                color: 'rgba(243,239,255,0.60)',
                                marginBottom: 7,
                                lineHeight: 1.35,
                              }}
                            >
                              {signal.label}
                            </div>
                            <div
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 7,
                                fontSize: 15,
                                fontWeight: 800,
                                color: '#F8F3FF',
                              }}
                            >
                              <span
                                aria-hidden="true"
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: 999,
                                  background: signal.value > 0 ? '#FFD700' : 'rgba(255,255,255,0.30)',
                                  boxShadow: signal.value > 0 ? '0 0 10px rgba(255,215,0,0.42)' : 'none',
                                }}
                              />
                              +{signal.value}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                        gap: 12,
                        marginBottom: 16,
                      }}
                    >
                      <div
                        style={{
                          borderRadius: 16,
                          padding: '14px 15px',
                          background: 'rgba(11, 7, 20, 0.58)',
                          border: '1px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        <div style={{ fontSize: 12, color: 'rgba(243,239,255,0.54)', marginBottom: 6 }}>
  {report.targetType === 'comment' ? 'Autor del post padre' : 'Autor del post'}
</div>
<div style={{ fontSize: 15, fontWeight: 700 }}>{report.authorName}</div>
<div style={{ fontSize: 12, color: 'rgba(243,239,255,0.54)', marginTop: 4 }}>
  ID: {report.authorId || '—'}
</div>
                      </div>

                      {report.targetType === 'comment' ? (
  <div
    style={{
      borderRadius: 16,
      padding: '14px 15px',
      background: 'rgba(11, 7, 20, 0.58)',
      border: '1px solid rgba(255,255,255,0.06)',
    }}
  >
    <div style={{ fontSize: 12, color: 'rgba(243,239,255,0.54)', marginBottom: 6 }}>Autor de la respuesta</div>
    <div style={{ fontSize: 15, fontWeight: 700 }}>{report.commentAuthorName || 'Usuario'}</div>
    <div style={{ fontSize: 12, color: 'rgba(243,239,255,0.54)', marginTop: 4 }}>
      ID: {report.commentAuthorId || '—'}
    </div>
  </div>
) : null}

                      <div
                        style={{
                          borderRadius: 16,
                          padding: '14px 15px',
                          background: 'rgba(11, 7, 20, 0.58)',
                          border: '1px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        <div style={{ fontSize: 12, color: 'rgba(243,239,255,0.54)', marginBottom: 6 }}>{adminText('Usuario que reportó', 'Reporter')}</div>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>{report.reporterName}</div>
                        <div style={{ fontSize: 12, color: 'rgba(243,239,255,0.54)', marginTop: 4 }}>
                          ID: {report.reporterId || '—'}
                        </div>
                      </div>

                      <div
                        style={{
                          borderRadius: 16,
                          padding: '14px 15px',
                          background: 'rgba(11, 7, 20, 0.58)',
                          border: '1px solid rgba(255,255,255,0.06)',
                        }}
                      >
                        <div style={{ fontSize: 12, color: 'rgba(243,239,255,0.54)', marginBottom: 6 }}>{adminText('Revisión', 'Review')}</div>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>
                          {report.canShowReviewedMeta ? formatDate(report.reviewedAt) : adminText('Todavía sin revisar', 'Not reviewed yet')}
                        </div>
                        <div style={{ fontSize: 12, color: 'rgba(243,239,255,0.54)', marginTop: 4 }}>
                          {adminText('Moderador:', 'Moderator:')} {report.canShowReviewedMeta ? report.reviewedByName : '—'}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        borderRadius: 18,
                        padding: '18px 18px 16px',
                        background: 'linear-gradient(180deg, rgba(23, 15, 39, 0.92), rgba(13, 8, 24, 0.9))',
                        border: '1px solid rgba(255,255,255,0.08)',
                        marginBottom: 14,
                      }}
                    >
                      <div style={{ fontSize: 12, color: 'rgba(243,239,255,0.56)', marginBottom: 8 }}>
  {report.targetType === 'comment' ? 'Respuesta reportada' : 'Contenido reportado'}
</div>
<div style={{ fontSize: 16, lineHeight: 1.72, whiteSpace: 'pre-wrap' }}>
  {report.targetType === 'comment'
    ? (report.commentText || 'Esta respuesta no trae texto visible en el reporte guardado.')
    : (report.postText || 'Esta publicación no trae texto visible en el reporte guardado.')}
</div>
{report.targetType === 'comment' && report.postText ? (
  <div
    style={{
      marginTop: 12,
      paddingTop: 12,
      borderTop: '1px solid rgba(255,255,255,0.08)',
    }}
  >
    <div style={{ fontSize: 12, color: 'rgba(243,239,255,0.56)', marginBottom: 8 }}>Post padre</div>
    <div style={{ fontSize: 14, lineHeight: 1.68, whiteSpace: 'pre-wrap', color: 'rgba(248,245,255,0.88)' }}>
      {report.postText}
    </div>
  </div>
) : null}
                    </div>

                    <div
                      style={{
                        borderRadius: 18,
                        padding: '16px 18px',
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        marginBottom: 18,
                      }}
                    >
                      <div style={{ fontSize: 12, color: 'rgba(243,239,255,0.56)', marginBottom: 8 }}>
                        Detalle del reporte
                      </div>
                      <div style={{ fontSize: 14, lineHeight: 1.72, color: 'rgba(248,245,255,0.92)' }}>
                        {report.details || 'El usuario no dejó detalle adicional en este reporte.'}
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 10,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => handleReview(report.reportId)}
                        disabled={isReviewing || isIgnoring || isDeleting}
                        style={{
                          ...baseButtonStyle,
                          background: 'rgba(54, 179, 126, 0.14)',
                          border: '1px solid rgba(54, 179, 126, 0.26)',
                          color: '#bbf3cf',
                          opacity: isReviewing || isIgnoring || isDeleting ? 0.72 : 1,
                        }}
                      >
                        {isReviewing ? 'Marcando...' : 'Marcar revisado'}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleIgnore(report.reportId)}
                        disabled={isReviewing || isIgnoring || isDeleting}
                        style={{
                          ...baseButtonStyle,
                          background: 'rgba(148, 163, 184, 0.12)',
                          border: '1px solid rgba(148, 163, 184, 0.24)',
                          color: '#dbe3f1',
                          opacity: isReviewing || isIgnoring || isDeleting ? 0.72 : 1,
                        }}
                      >
                        {isIgnoring ? 'Ignorando...' : 'Ignorar reporte'}
                      </button>

                      <button
  type="button"
  onClick={() =>
    report.targetType === 'comment'
      ? handleDeleteComment(report.commentId)
      : handleDeletePost(report.postId)
  }
  disabled={isReviewing || isIgnoring || isDeleting}
  style={{
    ...baseButtonStyle,
    background: 'rgba(255, 107, 107, 0.12)',
    border: '1px solid rgba(255, 107, 107, 0.24)',
    color: '#ffc2c2',
    opacity: isReviewing || isIgnoring || isDeleting ? 0.72 : 1,
  }}
>
  {isDeleting
    ? 'Eliminando...'
    : report.targetType === 'comment'
      ? 'Eliminar respuesta'
      : 'Eliminar post'}
</button>
                    </div>
                  </div>
                </article>
              );
            })}
        </div>
      </div>
    </div>
  );
}

export default AdminFeedReportsScreen;
